import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { setDirtyState } from '../../../store/slices/auth';
import classes from './Documents.module.css';
import cameraIcon from '../../../assets/icons/camera2.svg';
import plusIcon from '../../../assets/icons/plus-icon.svg';
import microphone from '../../../assets/icons/microphone.svg';
import {
  fetchDocuments,
  fetchDocumentsPerPerson,
  IDocument,
  IDocumentInput,
  selectDocStatus,
  selectDocuments,
  selectDocumentsPerPerson,
  selectDocumentToUpload,
  setDocumentsToUpload,
  uploadDocument,
  uploadDocumentsPerPerson,
} from '../../../store/slices/documents';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';

import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { DocumentList } from '../../../components/DocumentList/DocumentList';
import NewDocumentModal from '../../Consultations/Modals/NewDocumentModal';
import { VoiceRecorderModal } from '../../Consultations/Modals/VoiceRecorderModal';
import {
  groupDocumentsByDescription,
  groupDocumentsToUploadByDescription,
  isFileTypeAllowed,
  mergeDocumentGroupsByDescription,
} from '../../../utils/miscUtils';
import { toast } from 'react-toastify';
import { authorizeClient } from '../../../networking/client';
import { IAccountInfo, selectAccountInfo, selectDirtyState } from '../../../store/slices/auth';
import FileDropZone from '../../../components/UI/DropZone/DropZone';
import { ContentViewer } from '../../../components/ContentViewer/ContentViewer';
import Modal from '../../../components/UI/Modals/Modal';
import WithLoader from '../../../components/WithLoader/WithLoader';
import { useConsultationContext } from '../../../context/ConsultationContext';
import { ConsultationTypes, StatusIdTypes } from '../../../enums/enums';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  selectedSalesOrder?: SalesOrderInfo;
  personAccountNumber?: string;
};

const Documents: React.FC<Props> = ({ selectedSalesOrder, personAccountNumber }) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const { workOrderStatusId } = useConsultationContext();
  let isReports = window.location.pathname.includes('reports');

  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const documetsStatus = useAppSelector(selectDocStatus);

  const documents: IDocument[] = useAppSelector<IDocument[]>(selectDocuments);
  const documentsPerPerson: any[] = useAppSelector<any[]>(selectDocumentsPerPerson);

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [inputKey, setInputKey] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedFile, setSelectedFile] = React.useState<File>();
  const [isAddingDocument, setIsAddingDocument] = useState<boolean>(false);
  const [showVoiceRecorderModal, setShowVoiceRecorderModal] = useState<boolean>(false);
  const [documentAddVariant, setDocumentAddVariant] = useState<'capture' | 'upload'>('upload');
  const documentsToUpload: IDocumentInput[] = useAppSelector(selectDocumentToUpload);

  const isDirtyState = useAppSelector(selectDirtyState);

  const documentsToDisplay = personAccountNumber
    ? documentsPerPerson
      ? documentsPerPerson[0]?.attachments
      : []
    : documents || [];

  React.useEffect(() => {
    if (selectedFile) {
      setDocumentAddVariant('upload');
      setIsAddingDocument(true);
    }
  }, [selectedFile]);

  useEffect(() => {
    dispatch(
      personAccountNumber
        ? fetchDocumentsPerPerson({
            accountNumber: personAccountNumber,
          })
        : fetchDocuments({
            salesOrderId: selectedSalesOrder?.sales_order.sales_order_id ?? 0,
            salesOrderItemId: selectedSalesOrder?.sales_order_item.sales_order_item_id ?? 0,
          })
    );
  }, [dispatch]);

  const onFileChangeHandler = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (!isFileTypeAllowed(file.type)) {
        toast.error(translations('not.allowed.type'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }
      setSelectedFile(file);
    }
    setInputKey((prevKey) => prevKey + 1);
  };

  const bulkUpload = async () => {
    setIsLoading(true);
    const promises = documentsToUpload.map((document) => handleFileUpload(document));
    await Promise.allSettled(promises);
    dispatch(setDocumentsToUpload([]));
    setIsLoading(false);
    setInputKey((prevKey) => prevKey + 1);
  };

  const handleFileUpload = async (document: IDocumentInput) => {
    try {
      const formData = new FormData();
      formData.append(
        'document_request',
        JSON.stringify({
          attachment_type_id: document.document.attachment_type_id,
          document_title: document.document.title,
          document_description: document.document.comment,
          transcription_text: document.document.transcription_text,
        })
      );
      formData.append('file', document.file);

      const form = new FormData();
      form.append('filename_title', document.document.title);
      form.append('filename_description', document.document.comment);
      form.append('attachment', document.file);
      if (document.document.transcription_text)
        form.append('transcriptionText', document.document.transcription_text);
      if (document.document.transcriptionLanguageId)
        form.append(
          'transcriptionLangugageId',
          document.document.transcriptionLanguageId.toString()
        );

      await dispatch(
        personAccountNumber
          ? uploadDocumentsPerPerson({
              formData: formData,
              accountNumber: personAccountNumber,
            })
          : uploadDocument({
              formData: form,
              salesOrderId: selectedSalesOrder?.sales_order.sales_order_id ?? 0,
              salesOrderItemId:
                selectedSalesOrder?.sales_order_item?.external_sales_order_item_id ?? 0,
              attachmentTypeId: document.document.attachment_type_id,
            })
      );

      const ebaasForm = new FormData();
      const consultation_request = {
        ta_id: parseInt(selectedSalesOrder?.sales_order.ta_id?.toString() ?? ''),
        merchant_account_number: accountInfo?.account_number,
        product_order_id: parseInt(
          selectedSalesOrder?.sales_order.sales_order_external_id.toString() ?? ''
        ),
        product_order_item_id: parseInt(
          selectedSalesOrder?.sales_order_item?.external_sales_order_item_id.toString() ?? '0'
        ),
        document_title: document.document.title,
        document_description: document.document.comment,
        attachment_type_id: document.document.attachment_type_id,
      };
      ebaasForm.append('file', document.file);
      ebaasForm.append('consultation_request', JSON.stringify(consultation_request));
      !personAccountNumber &&
        (await authorizeClient.post(`/core/merchant/consultation/upload/document`, ebaasForm, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }));
    } catch (_) {}
  };

  const handleConfirmLeave = async () => {
    await bulkUpload();
    dispatch(setDocumentsToUpload([]));

    dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'documents' }));
  };

  const handleCancelLeave = () => {
    dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'documents' }));
  };

  const groupedDocuments = groupDocumentsByDescription(documentsToDisplay ?? []);
  const groupedDocumentsToUpload = groupDocumentsToUploadByDescription(documentsToUpload);
  const mergedDocuments = mergeDocumentGroupsByDescription(
    groupedDocuments,
    groupedDocumentsToUpload
  );

  const handleDelete = (documentId: string) => {
    dispatch(
      setDocumentsToUpload(documentsToUpload.filter((doc) => doc.document.id !== documentId))
    );
  };

  return (
    <WithLoader isLoading={documetsStatus === 'pending'}>
      {isReports &&
      documentsToDisplay &&
      documentsToDisplay.length === 0 &&
      selectedSalesOrder?.work_order?.work_order_status_id === 2 ? (
        <div className="emptyState">{translations('label.empty_state')}</div>
      ) : (
        <>
          <div className={classes.container}>
            <Modal
              hideCloseButton
              isOpen={isDirtyState.isModalOpen}
              onClose={() => {
                dispatch(
                  setDirtyState({ ...isDirtyState, isModalOpen: false, component: 'documents' })
                );
              }}
              maxWidth={'500px'}
              containerPadding={'50px'}
            >
              <div className={classes.text}>{translations('label.submit.files')}</div>
              <div className={classes.ModalButtonsContainer}>
                <div
                  className={`${classes.modalButtons} ${classes.submitButton}`}
                  onClick={() => handleConfirmLeave()}
                >
                  {translations('label.submit')}
                </div>
                <div
                  className={`${classes.modalButtons} ${classes.cancelButton}`}
                  onClick={() => handleCancelLeave()}
                >
                  {translations('button.cancel')}
                </div>
              </div>
            </Modal>
            <input
              name="filename"
              type="file"
              key={inputKey}
              ref={hiddenFileInput}
              style={{ display: 'none' }}
              onChange={onFileChangeHandler}
              accept="image/*,application/pdf"
            />
            {isReports && selectedSalesOrder?.work_order?.work_order_status_id === 2 ? null : (
              <div className={classes.contentHeader}>
                <div className={classes.floatRight}>
                  <button
                    disabled={isLoading}
                    className={classes.button}
                    onClick={() => hiddenFileInput?.current?.click()}
                  >
                    <img src={plusIcon} alt={'camera'} />
                    <div className={classes.buttonText}>{translations('file.upload')}</div>
                  </button>
                  <button
                    className={classes.button}
                    disabled={isLoading}
                    onClick={() => {
                      setDocumentAddVariant('capture');
                      setIsAddingDocument(true);
                    }}
                  >
                    <img src={cameraIcon} alt={'camera'} />
                    <div className={classes.buttonText}>{translations('capture')}</div>
                  </button>
                  <button
                    className={classes.button}
                    onClick={() => setShowVoiceRecorderModal(true)}
                    disabled={
                      selectedSalesOrder?.service_service_group.service_group_id !==
                        ConsultationTypes.FACE_TO_FACE_CONSULTATION &&
                      selectedSalesOrder?.service_service_group.service_group_id !==
                        ConsultationTypes.REVIEW_CONSULTATION &&
                      workOrderStatusId === StatusIdTypes.STARTED
                    }
                  >
                    <img src={microphone} alt={'microphone'} />
                    <div className={classes.buttonText}>{translations('voice.record')}</div>
                  </button>
                </div>
              </div>
            )}
            {documentsToDisplay &&
            documentsToDisplay.length === 0 &&
            documentsToUpload.length === 0 ? (
              <FileDropZone onFilesDropped={setSelectedFile} />
            ) : null}
            <div></div>

            {Object.keys(mergedDocuments).map((key) => {
              return (
                <ContentViewer
                  key={key}
                  title={translations(
                    `label.attachment_description_${key.replace(/ /g, '_').toLowerCase()}`
                  )}
                  content={''}
                  isCollapsible
                  style={{ margin: '10px 0px' }}
                >
                  <DocumentList
                    userInfo={selectedSalesOrder?.customer_person}
                    documents={mergedDocuments[key]}
                    fullWidth
                    handleDelete={handleDelete}
                  />
                </ContentViewer>
              );
            })}

            {isAddingDocument && (
              <div className={classes.modalContainer}>
                <NewDocumentModal
                  isOpen={isAddingDocument}
                  onClose={() => {
                    setIsAddingDocument(false);
                    setSelectedFile(undefined);
                  }}
                  variant={documentAddVariant}
                  selectedFile={selectedFile!}
                  setSelectedFile={setSelectedFile}
                  handleSave={(document) => {
                    const documentWithId: IDocumentInput = {
                      ...document,
                      document: {
                        ...document.document,
                        id: uuidv4(),
                      },
                      file: document.file,
                    };
                    dispatch(setDocumentsToUpload([...documentsToUpload, documentWithId]));
                    setIsAddingDocument(false);
                    setSelectedFile(undefined);
                    dispatch(
                      setDirtyState({ isDirty: true, isModalOpen: false, component: 'documents' })
                    );
                  }}
                />
              </div>
            )}
            {showVoiceRecorderModal && (
              <VoiceRecorderModal
                selectedSalesOrder={selectedSalesOrder}
                isOpen={showVoiceRecorderModal}
                onClose={() => setShowVoiceRecorderModal(false)}
                handleSave={(document) => {
                  dispatch(setDocumentsToUpload([...documentsToUpload, document]));
                  setIsAddingDocument(false);
                  setSelectedFile(undefined);
                  setShowVoiceRecorderModal(false);
                }}
              />
            )}
          </div>

          <PrimaryButtonV2
            text={'Submit'}
            containerStyle={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingBottom: '10px',
              paddingRight: '18px',
            }}
            buttonStyle={{ width: 250 }}
            onClick={handleConfirmLeave}
            disabled={documentsToUpload.length === 0 || isLoading}
          />
        </>
      )}
    </WithLoader>
  );
};
export { Documents };
